
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Search, RefreshCw, AlertCircle, Loader2, FlaskConical, Globe, Radar } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { Lead } from '@/types/lead';
import { StatusBadge } from './websearch/StatusBadge';
import LoadingState from './websearch/LoadingState';
import WebSearchDataView from './websearch/WebSearchDataView';
import ProcessingState from './websearch/ProcessingState';
import { useWebSearch } from '@/hooks/useWebSearch';
import { useAppSettings } from '@/hooks/useAppSettings';
import { triggerDeepResearch, triggerCrawl } from '@/services/webSearchService';
import ExtractionDataModal from './websearch/ExtractionDataModal';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

interface WebSearchPanelProps {
  lead: Lead;
  onDataUpdated?: () => void;
  onEnrichmentStatusChange?: (status: string | null, isProcessing: boolean, queuePosition: number | null) => void;
}

interface JobProgress {
  status: string;
  pagesScraped: number;
  totalPages: number;
  currentPage: string | null;
}

const WebSearchPanel: React.FC<WebSearchPanelProps> = ({ lead, onDataUpdated, onEnrichmentStatusChange }) => {
  const { toast } = useToast();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const { showLinkedInSection } = useAppSettings();
  const [isDeepResearchLoading, setIsDeepResearchLoading] = useState(false);
  const [isCrawlLoading, setIsCrawlLoading] = useState(false);
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);

  // Local status tracking
  const [localDeepResearchStatus, setLocalDeepResearchStatus] = useState<string | null>(lead.deepResearchStatus ?? null);
  const [localCrawlStatus, setLocalCrawlStatus] = useState<string | null>(lead.crawlStatus ?? null);
  const [crawlJobProgress, setCrawlJobProgress] = useState<JobProgress | null>(null);
  
  // Track web search job state for pipeline banner
  const [webSearchJobStep, setWebSearchJobStep] = useState<string | null>(null);

  // Sync from lead prop
  useEffect(() => {
    setLocalDeepResearchStatus(lead.deepResearchStatus ?? null);
  }, [lead.deepResearchStatus]);

  useEffect(() => {
    const rawStatus = lead.crawlStatus ?? null;
    const searchData = lead.webSearchData as any;
    const hasCrawlData = searchData?.crawlAnalysis && !searchData?.crawlAnalysis?.error;
    if (rawStatus === 'completed' && !hasCrawlData) {
      setLocalCrawlStatus(null);
    } else {
      setLocalCrawlStatus(rawStatus);
    }
  }, [lead.crawlStatus, lead.webSearchData]);



  // Realtime subscription for web_search enrichment_jobs progress
  useEffect(() => {
    const channel = supabase
      .channel(`enrichment-job-${lead.id}-ws-panel`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'enrichment_jobs',
        filter: `entity_id=eq.${lead.id}`,
      }, (payload) => {
        const job = payload.new as any;
        if (!job || job.job_type !== 'web_search') return;
        
        if (job.status === 'processing' || (job.status === 'completed' && job.current_page === 'step2_complete')) {
          setWebSearchJobStep(job.current_page || 'Processing...');
        } else if (job.status === 'completed' || job.status === 'failed') {
          // Only clear the step if truly done (not mid-pipeline)
          const page = job.current_page;
          if (page !== 'step1_complete' && page !== 'firecrawl_search_start') {
            setWebSearchJobStep(null);
          } else {
            setWebSearchJobStep(page);
          }
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [lead.id]);

  // Auto-fail stale processing statuses (no active job for 15+ minutes)
  useEffect(() => {
    const checkStale = async (statusField: 'deep_research_status' | 'crawl_status', jobType: string, setLocal: (s: string | null) => void, label: string) => {
      // FIRST: check if the DB already shows completed (prevents race condition
      // where cached lead data shows 'processing' but backend already finished)
      const { data: leadRow } = await supabase
        .from('leads')
        .select(statusField)
        .eq('id', lead.id)
        .single();

      if (leadRow?.[statusField] === 'completed') {
        console.log(`[AutoFail] ${label} already completed in DB for lead ${lead.id} — syncing local state`);
        setLocal('completed');
        return;
      }

      // Query for active enrichment_jobs
      const { data: jobs } = await supabase
        .from('enrichment_jobs')
        .select('id, status, updated_at')
        .eq('entity_id', lead.id)
        .eq('job_type', jobType)
        .in('status', ['pending', 'scraping', 'extracting', 'merging', 'processing'])
        .order('created_at', { ascending: false })
        .limit(1);

      const activeJob = jobs?.[0];
      const now = Date.now();
      const isStale = !activeJob || (now - new Date(activeJob.updated_at).getTime() > 15 * 60 * 1000);

      if (isStale) {
        console.warn(`[AutoFail] ${label} is stale for lead ${lead.id} — marking as failed`);
        await supabase
          .from('leads')
          .update({ [statusField]: 'failed', updated_at: new Date().toISOString() })
          .eq('id', lead.id);
        setLocal('failed');
        toast({ variant: "destructive", title: `${label} appears stalled`, description: "No progress detected — click to retry." });
      }
    };

    if (localDeepResearchStatus === 'processing') {
      checkStale('deep_research_status', 'deep_research', setLocalDeepResearchStatus, 'Deep research');
    }
    if (localCrawlStatus === 'processing') {
      checkStale('crawl_status', 'crawl', setLocalCrawlStatus, 'Website crawl');
    }
  }, [lead.id]); // Only on mount / lead change

  // Realtime subscription for deep research progress via enrichment_jobs
  useEffect(() => {
    if (localDeepResearchStatus !== 'processing') {
      setJobProgress(null);
      return;
    }

    const channel = supabase
      .channel(`enrichment-job-${lead.id}-dr`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'enrichment_jobs',
        filter: `entity_id=eq.${lead.id}`,
      }, (payload) => {
        const job = payload.new as any;
        if (!job || job.job_type !== 'deep_research') return;

        setJobProgress({
          status: job.status,
          pagesScraped: job.pages_scraped,
          totalPages: job.total_pages,
          currentPage: job.current_page,
        });

        if (job.status === 'completed') {
          setLocalDeepResearchStatus('completed');
          toast({ title: "Deep research complete", description: `Insights extracted for ${lead.name}` });
          refreshSearchData();
          onDataUpdated?.();
        } else if (job.status === 'failed') {
          setLocalDeepResearchStatus('failed');
          toast({ variant: "destructive", title: "Deep research failed", description: job.error_message || "The background job encountered an error." });
          onDataUpdated?.();
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [localDeepResearchStatus, lead.id, lead.name, toast, onDataUpdated]);

  // Track the active crawl job ID for filtering realtime events
  const [activeCrawlJobId, setActiveCrawlJobId] = useState<string | null>(null);
  const lastCrawlProgressRef = useRef<number>(Date.now());

  // Realtime subscription for crawl progress via enrichment_jobs
  useEffect(() => {
    if (localCrawlStatus !== 'processing') {
      setCrawlJobProgress(null);
      return;
    }

    // Reset progress tracker
    lastCrawlProgressRef.current = Date.now();

    const channel = supabase
      .channel(`enrichment-job-${lead.id}-crawl`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'enrichment_jobs',
        filter: `entity_id=eq.${lead.id}`,
      }, (payload) => {
        const job = payload.new as any;
        if (!job || job.job_type !== 'crawl') return;

        // Only process events for the active job (if we have one)
        if (activeCrawlJobId && job.id !== activeCrawlJobId) return;

        // Track progress time for inactivity watchdog
        lastCrawlProgressRef.current = Date.now();

        setCrawlJobProgress({
          status: job.status,
          pagesScraped: job.pages_scraped,
          totalPages: job.total_pages,
          currentPage: job.current_page,
        });

        if (job.status === 'completed') {
          setLocalCrawlStatus('completed');
          setActiveCrawlJobId(null);
          toast({ title: "Website crawl complete", description: `Pages crawled and analyzed for ${lead.name}` });
          refreshSearchData();
          onDataUpdated?.();
        } else if (job.status === 'failed') {
          setLocalCrawlStatus('failed');
          setActiveCrawlJobId(null);
          toast({ variant: "destructive", title: "Website crawl failed", description: job.error_message || "The background job encountered an error." });
          onDataUpdated?.();
        }
      })
      .subscribe();

    // Inactivity-based watchdog: warn if no progress for 5 minutes, but DON'T mutate DB
    const watchdogInterval = setInterval(() => {
      const elapsed = Date.now() - lastCrawlProgressRef.current;
      if (elapsed > 5 * 60 * 1000) {
        console.warn('[CrawlRT] No crawl progress for 5 minutes — showing warning');
        setLocalCrawlStatus('failed');
        setActiveCrawlJobId(null);
        toast({ variant: "destructive", title: "Website crawl appears stalled", description: "No progress received. The backend may still be working — check again shortly." });
        clearInterval(watchdogInterval);
      }
    }, 30 * 1000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(watchdogInterval);
    };
  }, [localCrawlStatus, lead.id, lead.name, toast, onDataUpdated, activeCrawlJobId]);

  const handleDeepResearch = async () => {
    setIsDeepResearchLoading(true);
    try {
      const success = await triggerDeepResearch(lead.id);
      if (success) {
        toast({ title: "Deep research started", description: "Finding key people & scraping website in the background..." });
        setLocalDeepResearchStatus('processing');
        onDataUpdated?.();
        // Check if the backend immediately skipped/failed (e.g. too many prior failures)
        setTimeout(async () => {
          const { data } = await supabase
            .from('leads')
            .select('deep_research_status')
            .eq('id', lead.id)
            .single();
          if (data && (data.deep_research_status === 'skipped' || data.deep_research_status === 'failed')) {
            setLocalDeepResearchStatus(data.deep_research_status);
          }
        }, 3000);
      } else {
        toast({ variant: "destructive", title: "Deep research failed", description: "Could not start deep research." });
      }
    } catch (err) {
      console.error('[DeepResearch UI] Exception:', err);
      toast({ variant: "destructive", title: "Deep research failed", description: "An unexpected error occurred." });
    } finally {
      setIsDeepResearchLoading(false);
    }
  };

  const handleCrawl = async () => {
    if (!lead.website) {
      toast({ variant: "destructive", title: "No website", description: "Run Deep Research first to discover the website." });
      return;
    }
    setIsCrawlLoading(true);
    try {
      const result = await triggerCrawl(lead.id);
      if (result.ok) {
        toast({ title: "Website crawl started", description: "Crawling website pages in the background..." });
        setLocalCrawlStatus('processing');
        if (result.jobId) setActiveCrawlJobId(result.jobId);
        onDataUpdated?.();
      } else {
        toast({ variant: "destructive", title: "Crawl failed", description: result.error || "Could not start website crawl." });
      }
    } catch (err) {
      console.error('[Crawl UI] Exception:', err);
      toast({ variant: "destructive", title: "Crawl failed", description: "An unexpected error occurred." });
    } finally {
      setIsCrawlLoading(false);
    }
  };

  const {
    searchData,
    searchStatus,
    lastUpdated,
    queuedAt,
    isLoading,
    isProcessing,
    isInitialLoading,
    progressValue,
    queuePosition,
    handleRefresh,
    handleProcessQueue,
    refreshSearchData,
    jobState,
  } = useWebSearch(lead, onDataUpdated, showLinkedInSection);

  // Realtime subscription for lead-level field changes (deep_research_status, crawl_status)
  useEffect(() => {
    const channel = supabase
      .channel(`lead-enrichment-stage-${lead.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'leads',
        filter: `id=eq.${lead.id}`,
      }, (payload) => {
        const updated = payload.new as any;
        if (updated.deep_research_status) {
          setLocalDeepResearchStatus(updated.deep_research_status);
          if (updated.deep_research_status === 'completed') {
            refreshSearchData();
            onDataUpdated?.();
          }
        }
        if (updated.crawl_status) {
          setLocalCrawlStatus(updated.crawl_status);
          if (updated.crawl_status === 'completed') {
            refreshSearchData();
            onDataUpdated?.();
          }
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [lead.id, refreshSearchData, onDataUpdated]);

  useEffect(() => {
    if (onEnrichmentStatusChange) {
      onEnrichmentStatusChange(searchStatus, isProcessing || isLoading, queuePosition);
    }
  }, [searchStatus, isProcessing, isLoading, queuePosition, onEnrichmentStatusChange]);

  // Watchdog: auto-clear stuck web search job step after 5 minutes
  useEffect(() => {
    if (webSearchJobStep && searchStatus === 'completed') {
      const timer = setTimeout(() => {
        console.log('[WebSearchPanel] Watchdog: clearing stuck webSearchJobStep after 5 min');
        setWebSearchJobStep(null);
      }, 5 * 60 * 1000);
      return () => clearTimeout(timer);
    }
  }, [webSearchJobStep, searchStatus]);

  // Compute enrichment pipeline stage for progress banner
  const pipelineStage = useMemo(() => {
    const step = jobState.currentPage || webSearchJobStep;
    // Step 1: Gemini search (pending/processing, or any step before step1_complete)
    if (!step && (searchStatus === 'pending' || searchStatus === 'processing')) return 1;
    if (jobState.status === 'processing' && step && !step.includes('step') && !step.includes('firecrawl')) return 1;
    // Step 2: Firecrawl search
    if (step === 'step1_complete' || step?.includes('firecrawl') || step === 'create_contacts_start' || step === 'create_contacts_complete') return 2;
    if (step === 'step2_complete') return 2;
    // Step 3: Deep research (only while actively processing, not skipped/failed)
    if (localDeepResearchStatus === 'processing') return 3;
    return null; // No active pipeline
  }, [searchStatus, webSearchJobStep, localDeepResearchStatus, jobState]);

  // Map job step to human-readable label
  const webSearchStepLabel = useMemo(() => {
    if (!webSearchJobStep) return null;
    const stepMap: Record<string, string> = {
      'gemini_search_start': 'Searching the web with AI...',
      'gemini_search_complete': 'Web search complete, extracting contacts...',
      'contact_extraction_start': 'Extracting contacts with AI...',
      'contact_extraction_complete': 'Contacts extracted',
      'linkedin_enrichment_start': 'Enriching LinkedIn data...',
      'linkedin_enrichment_complete': 'LinkedIn enrichment complete',
      'data_quality_assessment': 'Assessing data quality...',
      'save_to_db': 'Saving results...',
      'step1_complete': 'Web search saved, starting deep scan...',
      'firecrawl_search_start': 'Deep-scanning contacts & pages...',
      'firecrawl_search_complete': 'Deep scan complete',
      'create_contacts_start': 'Saving key contacts...',
      'create_contacts_complete': 'Contacts saved',
      'step2_complete': 'Deep scan complete',
      'deep_research_triggered': 'Starting deep research...',
      'completed': 'Enrichment complete',
    };
    return stepMap[webSearchJobStep] || webSearchJobStep;
  }, [webSearchJobStep]);

  if (isInitialLoading) {
    return <LoadingState />;
  }

  if (searchData) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center">
                <Search className="h-5 w-5 mr-2" />
                Company Insight
              </CardTitle>
              <CardDescription className="flex items-center">
                {lastUpdated && (
                  <span className="mr-2">Last updated {format(lastUpdated, 'MMM d, yyyy HH:mm')}</span>
                )}
                <StatusBadge status={searchStatus} />
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (<TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeepResearch}
                      disabled={isDeepResearchLoading || localDeepResearchStatus === 'processing'}
                      className={`relative ${localDeepResearchStatus === 'completed' ? 'border-purple-400 dark:border-purple-600' : localDeepResearchStatus === 'skipped' ? 'border-amber-400 dark:border-amber-600' : ''}`}
                    >
                      {isDeepResearchLoading || localDeepResearchStatus === 'processing' ? (
                        <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                      ) : localDeepResearchStatus === 'skipped' ? (
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                      ) : (
                        <FlaskConical className="h-4 w-4 text-purple-600" />
                      )}
                      {localDeepResearchStatus === 'completed' && (
                        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-background" />
                      )}
                      {localDeepResearchStatus === 'processing' && (
                        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-purple-500 animate-pulse border-2 border-background" />
                      )}
                      {localDeepResearchStatus === 'skipped' && (
                        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-500 border-2 border-background" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {localDeepResearchStatus === 'processing'
                      ? 'Deep research in progress...'
                      : localDeepResearchStatus === 'completed'
                        ? 'Deep research complete — click to re-run'
                        : localDeepResearchStatus === 'skipped'
                          ? 'Deep research skipped after repeated failures — click to force retry'
                          : localDeepResearchStatus === 'failed'
                            ? 'Deep research failed — click to retry'
                            : 'Run Deep Research'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>)}
              {isAdmin && (<TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCrawl}
                      disabled={isCrawlLoading || localCrawlStatus === 'processing' || !lead.website}
                      className={`relative ${localCrawlStatus === 'completed' ? 'border-teal-400 dark:border-teal-600' : ''}`}
                    >
                      {isCrawlLoading || localCrawlStatus === 'processing' ? (
                        <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
                      ) : (
                        <Globe className="h-4 w-4 text-teal-600" />
                      )}
                      {localCrawlStatus === 'completed' && (
                        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-background" />
                      )}
                      {localCrawlStatus === 'processing' && (
                        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-teal-500 animate-pulse border-2 border-background" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {localCrawlStatus === 'processing'
                      ? 'Website crawl in progress...'
                      : localCrawlStatus === 'completed'
                        ? 'Crawl complete — click to re-run'
                        : localCrawlStatus === 'failed'
                          ? 'Crawl failed — click to retry'
                          : 'Run Website Crawl'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>)}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh} 
                disabled={isLoading || searchStatus === 'pending' || searchStatus === 'processing'}
                title="Refresh web information"
              >
                {isLoading || searchStatus === 'pending' || searchStatus === 'processing' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Enrichment Pipeline Progress Banner */}
          {pipelineStage !== null && (
            <div className="mb-4 p-3 rounded-lg bg-black/40 border border-white/10 space-y-3">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-amber-400" />
                <span className="text-sm font-medium text-white">
                  {pipelineStage === 1 && (webSearchStepLabel || 'Step 1/3: Searching the web...')}
                  {pipelineStage === 2 && (webSearchStepLabel || 'Step 2/3: Deep-scanning contacts & pages...')}
                  {pipelineStage === 3 && 'Step 3/3: Deep research in progress...'}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center gap-1.5">
                    <div className={cn(
                      "h-2 w-2 rounded-full transition-all",
                      step < pipelineStage! ? "bg-emerald-400" :
                      step === pipelineStage ? "bg-amber-400 animate-pulse" :
                      "bg-white/20"
                    )} />
                    <span className={cn(
                      "text-xs",
                      step < pipelineStage! ? "text-emerald-400" :
                      step === pipelineStage ? "text-white" : "text-white/40"
                    )}>
                      {step === 1 ? 'Web Search' : step === 2 ? 'Deep Scan' : 'Deep Research'}
                    </span>
                    {step < 3 && <div className={cn("w-6 h-px", step < pipelineStage! ? "bg-emerald-400" : "bg-white/15")} />}
                  </div>
                ))}
              </div>
            </div>
          )}
          {localDeepResearchStatus === 'processing' && (
            <div className="mb-4 p-3 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 space-y-2">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-purple-600 dark:text-purple-400" />
                <span className="text-sm text-purple-700 dark:text-purple-300 font-medium">
                  {jobProgress?.status === 'scraping' && jobProgress.totalPages > 0
                    ? `Scraping page ${jobProgress.pagesScraped + 1} of ${jobProgress.totalPages}...`
                    : jobProgress?.status === 'extracting'
                      ? 'Extracting structured data with AI...'
                      : jobProgress?.status === 'merging'
                        ? 'Merging findings into company profile...'
                        : 'Deep research in progress — finding key people & scraping website...'}
                </span>
              </div>
              {jobProgress && jobProgress.totalPages > 0 && (
                <div className="space-y-1">
                  <Progress value={(jobProgress.pagesScraped / jobProgress.totalPages) * 100} className="h-1.5" />
                  {jobProgress.currentPage && (
                    <p className="text-xs text-purple-600/70 dark:text-purple-400/70 truncate">{jobProgress.currentPage}</p>
                  )}
                </div>
              )}
            </div>
          )}
          {localDeepResearchStatus === 'completed' && (
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">
                <FlaskConical className="h-3 w-3 mr-1" />
                Deep Research Complete
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-purple-600 hover:text-purple-700"
                onClick={handleDeepResearch}
                disabled={isDeepResearchLoading}
              >
                {isDeepResearchLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Re-run
              </Button>
              <ExtractionDataModal searchData={searchData} defaultTab="deep-research" entityId={lead.id} />
            </div>
          )}
          {localDeepResearchStatus === 'skipped' && (
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">
                <AlertCircle className="h-3 w-3 mr-1" />
                Deep Research Skipped
              </Badge>
              <span className="text-xs text-muted-foreground">Too many failed attempts</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700"
                onClick={handleDeepResearch}
                disabled={isDeepResearchLoading}
              >
                {isDeepResearchLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Force retry
              </Button>
            </div>
          )}
          {localCrawlStatus === 'processing' && (
            <div className="mb-4 p-3 rounded-lg bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800 space-y-2">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-teal-600 dark:text-teal-400" />
                <span className="text-sm text-teal-700 dark:text-teal-300 font-medium">
                  {crawlJobProgress?.status === 'scraping' && crawlJobProgress.totalPages > 0
                    ? `Scraping page ${crawlJobProgress.pagesScraped + 1} of ${crawlJobProgress.totalPages}...`
                    : 'Website crawl in progress — scraping pages...'}
                </span>
              </div>
              {crawlJobProgress && crawlJobProgress.totalPages > 0 && (
                <div className="space-y-1">
                  <Progress value={(crawlJobProgress.pagesScraped / crawlJobProgress.totalPages) * 100} className="h-1.5" />
                  {crawlJobProgress.currentPage && (
                    <p className="text-xs text-teal-600/70 dark:text-teal-400/70 truncate">{crawlJobProgress.currentPage}</p>
                  )}
                </div>
              )}
            </div>
          )}
          {localCrawlStatus === 'completed' && (
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-300 dark:border-teal-800">
                <Globe className="h-3 w-3 mr-1" />
                Crawl Complete{searchData?.crawlPageCount ? ` (${searchData.crawlPageCount} pages)` : ''}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-teal-600 hover:text-teal-700"
                onClick={handleCrawl}
                disabled={isCrawlLoading || !lead.website}
              >
                {isCrawlLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Re-run
              </Button>
              <ExtractionDataModal searchData={searchData} defaultTab="crawl" entityId={lead.id} />
            </div>
          )}
          <WebSearchDataView
            searchData={searchData}
            leadName={lead.name}
            keyContacts={lead.keyContacts}
            leadAddress={lead.address}
            leadPhone={lead.phone}
            leadEmail={typeof lead.email === 'string' ? lead.email : ''}
            leadWebsite={lead.website}
            editable={true}
            onSaveSummary={async (newSummary: string) => {
              try {
                const currentData = (lead.webSearchData || searchData || {}) as Record<string, any>;
                const updatedData = { ...currentData, summary: newSummary };
                const { error } = await supabase
                  .from('leads')
                  .update({ web_search_data: updatedData })
                  .eq('id', lead.id);
                if (error) throw error;
                toast({ description: 'Summary saved' });
                refreshSearchData();
                onDataUpdated?.();
              } catch (err) {
                console.error('Failed to save summary:', err);
                toast({ variant: 'destructive', title: 'Error', description: 'Failed to save summary' });
              }
            }}
          />
        </CardContent>
      </Card>
    );
  }

  if (!searchData && (searchStatus === 'pending' || searchStatus === 'processing')) {
    return (
      <ProcessingState
        searchStatus={searchStatus}
        progressValue={progressValue}
        queuePosition={queuePosition}
        queuedAt={queuedAt}
        isLoading={isLoading}
        isProcessing={isProcessing}
        onRefresh={handleRefresh}
        onProcessQueue={handleProcessQueue}
      />
    );
  }

  if (!searchData && searchStatus === 'failed') {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Company Insight
          </CardTitle>
          <CardDescription className="flex items-center">
            <span className="mr-2">Web search failed</span>
            <StatusBadge status={searchStatus} />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <AlertCircle className="h-10 w-10 mb-4 text-red-500" />
            <h3 className="text-lg font-medium mb-2">Web search failed</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              There was an error retrieving web information for this lead.
              You can try again using the button below.
            </p>
            <Button onClick={handleRefresh} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry Web Search
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <Search className="h-5 w-5 mr-2" />
          Company Insight
        </CardTitle>
        <CardDescription>
          Enrich this lead with information from the web
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Search className="h-10 w-10 mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">No web information available</h3>
          <p className="text-muted-foreground mb-6 max-w-md">
            Get detailed information about this lead from across the web, including services, key people, reviews, and more.
          </p>
          <div className="flex flex-col gap-3 items-center">
            <Button onClick={handleRefresh} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Searching the web...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Perform Web Search
                </>
              )}
            </Button>
            {lead.id && (
              <Button
                variant="outline"
                onClick={handleDeepResearch}
                disabled={isDeepResearchLoading || localDeepResearchStatus === 'processing'}
              >
                {isDeepResearchLoading || localDeepResearchStatus === 'processing' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin text-purple-600" />
                    Running Deep Research...
                  </>
                ) : (
                  <>
                    <FlaskConical className="h-4 w-4 mr-2 text-purple-600" />
                    Run Deep Research
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WebSearchPanel;
